import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { useRef } from "react";
import { api } from "@/lib/kfmApi";
import { Languages } from "lucide-react";
import PronounceButton from "@/components/PronounceButton";
import BookmarkButton from "@/components/BookmarkButton";
import CopyButton from "@/components/CopyButton";
import { buildStrongsText } from "@/lib/studyText";
import ConcordanceDialog from "@/components/ConcordanceDialog";

export default function StrongsAccordion({ entries, sessionId, reference }) {
  const seen = useRef(new Set());
  if (!entries || entries.length === 0) return null;

  // Cross-session word tracking (item 2): record each Strong's entry the first
  // time it is expanded in this session.
  const onOpen = (openValues) => {
    (openValues || []).forEach((val) => {
      const idx = parseInt(val.replace("strongs-", ""), 10);
      const e = entries[idx];
      if (!e || !e.strongs_number || seen.current.has(e.strongs_number)) return;
      seen.current.add(e.strongs_number);
      if (sessionId) {
        api
          .post(`/study/sessions/${sessionId}/word-seen`, {
            strongs_number: e.strongs_number,
            word: e.word || "",
            reference: reference || "",
          })
          .catch(() => {});
      }
    });
  };

  return (
    <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] px-5 py-2">
      <div className="flex items-center gap-2 py-3 text-xs font-medium uppercase tracking-widest text-slate-500">
        <Languages className="w-4 h-4" strokeWidth={1.5} />
        Original language · Strong's
      </div>
      <Accordion type="multiple" className="w-full" onValueChange={onOpen}>
        {entries.map((e, i) => (
          <AccordionItem key={i} value={`strongs-${i}`} className="border-[#1E273D]">
            <AccordionTrigger
              data-testid={`strongs-trigger-${i}`}
              className="hover:no-underline py-4 text-left"
            >
              <span className="flex items-center gap-3">
                <span className="text-lg text-slate-200">{e.original}</span>
                <span className="text-sm text-slate-400">{e.word}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent data-testid={`strongs-content-${i}`} className="text-sm text-slate-400 space-y-2 pb-5">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="text-base text-slate-300">{e.original}</span>
                <PronounceButton entry={e} testid={`strongs-pronounce-${i}`} />
                <BookmarkButton
                  testid={`strongs-save-${i}`}
                  payload={{
                    kind: "word",
                    strongs_number: e.strongs_number || "",
                    word: e.word || "",
                    original: e.original || "",
                    transliteration: e.transliteration || "",
                    definition: e.definition || "",
                  }}
                />
                <CopyButton
                  text={buildStrongsText(e)}
                  label="Word copied"
                  testid={`strongs-copy-${i}`}
                  title="Copy this word study"
                />
                <span>{e.transliteration}</span>
                <span className="font-medium text-[#00E5FF]">{e.strongs_number}</span>
              </div>
              <p className="leading-relaxed">
                <span className="font-medium text-slate-300">Definition. </span>
                {e.definition}
              </p>
              {e.usage && (
                <p className="leading-relaxed">
                  <span className="font-medium text-slate-300">In this passage. </span>
                  {e.usage}
                </p>
              )}
              {e.strongs_number && (
                <div className="pt-1">
                  <ConcordanceDialog
                    number={e.strongs_number}
                    label="View sourced entry & Word Trail"
                    sessionId={sessionId}
                    testid={`strongs-concordance-${i}`}
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
